import React, { useState } from "react";
import { Button, Icon, useToast } from "@chakra-ui/react";
import { FaFileExcel } from "react-icons/fa";
import useAxios from "axios-hooks";
import * as XLSX from "xlsx";
import logger from "infra/logger";
import RoleBasedRender from "components/wrappers/roleBasedRender";
import { onlyUAdmins } from "utils/accessLevels";

const PayrunExportButton = ({ payrunId, month, year }) => {
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  const [{ loading }, fetchExportData] = useAxios(
    {
      url: `/api/payrun/${payrunId}/export`,
      method: "GET",
    },
    { manual: true },
  );

  const handleExport = async () => {
    if (!payrunId) {
      toast({
        title: "Error",
        description: "Payrun ID is required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsExporting(true);

    try {
      const response = await fetchExportData();
      const exportData = response.data.data;

      if (!exportData || exportData.length === 0) {
        toast({
          title: "No Data",
          description: "No payout data available to export",
          status: "warning",
          duration: 3000,
          isClosable: true,
        });
        setIsExporting(false);
        return;
      }

      // Transform data for Excel
      const excelData = exportData.map((row) => ({
        "S.No": row.s_no,
        "Person Name": row.person_name,
        Type: row.type || "N/A",
        "Location Name": row.location_name,
        "Station ID": row.station_id,
        "Total Amount": row.total_amount?.toFixed(2) || "0.00",
        "Bank Account Number": row.bank_account_number || "N/A",
        "IFSC Code": row.ifsc_code || "N/A",
        Status: row.status,
      }));

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Payouts");

      // Set column widths
      const columnWidths = [
        { wch: 8 }, // S.No
        { wch: 25 }, // Person Name
        { wch: 25 }, // Type
        { wch: 30 }, // Location Name
        { wch: 15 }, // Station ID
        { wch: 15 }, // Total Amount
        { wch: 20 }, // Bank Account Number
        { wch: 15 }, // IFSC Code
        { wch: 15 }, // Status
      ];
      worksheet["!cols"] = columnWidths;

      // Generate filename with month and year
      const filename = `Payouts_${month}_${year}.xlsx`;

      // Download file
      XLSX.writeFile(workbook, filename);

      toast({
        title: "Export Successful",
        description: `Exported ${exportData.length} payout records`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      logger.error("Error exporting payrun data:", error);
      toast({
        title: "Export Failed",
        description:
          error?.response?.data?.error || "Failed to export payout data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <RoleBasedRender roles={onlyUAdmins}>
      <Button
        leftIcon={<Icon as={FaFileExcel} boxSize={3} />}
        onClick={handleExport}
        isLoading={loading || isExporting}
        loadingText="Exporting..."
        colorScheme="green"
        variant="solid"
        size="sm"
        fontSize="xs"
        borderRadius="md"
        px={4}
        py={2}
        _hover={{
          transform: "translateY(-2px)",
          boxShadow: "lg",
        }}
        transition="all 0.2s"
      >
        Export
      </Button>
    </RoleBasedRender>
  );
};

export default PayrunExportButton;
